/**
 * @author Sambit Ekka <sambit@314ecorp.com>
 * @description Global Fabric.js defaults + web-font preloading. Imported for
 * side-effects from main.tsx so every Textbox instance inherits consistent,
 * readable metrics regardless of which call site constructs it.
 */

import { cache, Textbox } from 'fabric';

/**
 * Fabric's built-in default is 1.16, which is too tight for multi-line annotation
 * text — descenders of one line collide with ascenders of the next, producing
 * visually uneven spacing. 1.3 keeps lines clearly separated across all fonts.
 */
export const ANNOTATION_TEXT_LINE_HEIGHT = 1.3;

Textbox.prototype.lineHeight = ANNOTATION_TEXT_LINE_HEIGHT;

/**
 * Google Fonts serves Roboto (and the other editor fonts) via @font-face rules
 * with `unicode-range` subsetting: the browser only downloads a subset when the
 * DOM actually renders a character from that range. Canvas `measureText` does
 * NOT trigger those downloads. So when a Fabric Textbox measures a character
 * for a font whose subset has not been requested yet, the browser returns a
 * metric-compatible FALLBACK width (e.g. "e" at 40px Roboto returns ~17.75px
 * unloaded vs ~21.15px loaded — a ~19% error). Fabric caches that wrong width
 * under the Roboto key. Once any DOM element triggers the real subset to load,
 * subsequent re-measurements return the correct width — and the mix of cached
 * fallback widths and fresh real widths produces visibly uneven letter spacing
 * during typing, most obvious for very common letters like "e".
 *
 * Fix: proactively force each (family, weight, style) combination the editor
 * supports to download at boot so canvas measurements match rendered glyphs.
 * Also clear Fabric's font cache whenever any font finishes loading later
 * (e.g. a user picks a non-default family).
 */
const EDITOR_FONT_FAMILIES = ['Roboto', 'Inter', 'Arial', 'Playfair Display', 'Nunito Sans', 'Montserrat'] as const;
const EDITOR_FONT_WEIGHTS = ['400', '500', '700'] as const;
const EDITOR_FONT_STYLES = ['normal', 'italic'] as const;
const FONT_PROBE_SIZE_PX = 16;

/**
 * Probe text that forces the browser to download the common unicode-range
 * subsets Google Fonts ships per family. Without this, only the Latin subset
 * that covers the probe's default space character would be fetched, leaving
 * diacritics, Cyrillic, and Greek subsets lazy-loaded — and vulnerable to the
 * same stale-measurement bug the first time a user types a character from
 * them. Covered ranges: Latin (A), Latin-ext (À), Cyrillic (А), Greek (α).
 */
const FONT_PROBE_TEXT = 'AÀАα';

const buildFontDescriptors = (): string[] => {
	const descriptors: string[] = [];
	for (const family of EDITOR_FONT_FAMILIES) {
		for (const weight of EDITOR_FONT_WEIGHTS) {
			for (const style of EDITOR_FONT_STYLES) {
				descriptors.push(`${style} ${weight} ${FONT_PROBE_SIZE_PX}px "${family}"`);
			}
		}
	}
	return descriptors;
};

export const preloadEditorFonts = async (): Promise<void> => {
	if (typeof document === 'undefined' || !document.fonts?.load) {
		return;
	}
	const descriptors = buildFontDescriptors();
	await Promise.all(descriptors.map((descriptor) => document.fonts.load(descriptor, FONT_PROBE_TEXT).catch(() => undefined)));
	cache.clearFontCache();
};

export const subscribeFabricCacheToFontLoads = (): void => {
	if (typeof document === 'undefined' || !document.fonts?.addEventListener) {
		return;
	}
	document.fonts.addEventListener('loadingdone', () => {
		cache.clearFontCache();
	});
};

preloadEditorFonts();
subscribeFabricCacheToFontLoads();
